import { useFormContext } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../../../components";
import {
  gridSize,
  MAX_LENGTH_15,
  MAX_LENGTH_75,
} from "../../../../../../../../helpers/constants";
import { validateAndFormatNumericInput } from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { ProjectAddOrEditExpenseOrFeeFormKey } from "../../../../../../modelsAndSchemas/Models";
import { projectAddFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

interface GeneralFromExpenseDetailsFieldsProps {
  categoryOptions: OptionsDataType[];
  typeOptions: OptionsDataType[];
  children: string | React.JSX.Element | React.JSX.Element[];
}

export const GeneralFromExpenseDetailsFields = ({
  children: currencyAndAmountFields,
  categoryOptions,
  typeOptions,
}: GeneralFromExpenseDetailsFieldsProps) => {
  const { useFetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { control, setValue } = useFormContext();

  const getLabel = useFetchLabel(
    projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
  );

  const handleOnChangeUnitPrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseUnitPrice,
      validateAndFormatNumericInput(value, MAX_LENGTH_15),
    );
  };

  return (
    <MuiBox className="form-section">
      <MuiBox className="form-section-header">
        <MuiTypography variant="h2SemiBold">
          {getLabel(
            projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsHeading,
          )}
        </MuiTypography>
      </MuiBox>
      <MuiGrid container spacing={2}>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseCategoryId}
          >
            <MuiSelect
              label={getLabel(
                projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsCategory,
              )}
              options={categoryOptions}
              visibleDefaultSelectOption
              placeholder={commonLabels.selectPlaceHolder}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} spacing={2}>
          <FormController
            control={control}
            name={ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseTypeId}
          >
            <MuiSelect
              label={getLabel(
                projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsType,
              )}
              options={typeOptions}
              visibleDefaultSelectOption
              placeholder={commonLabels.selectPlaceHolder}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} spacing={2}>
          <FormController
            control={control}
            name={ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseUnitPrice}
          >
            <MuiTextField
              id={ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseUnitPrice}
              isMandatory
              label={getLabel(
                projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsUnitPrice,
              )}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              type="number"
              positiveValuesOnly
              onChange={handleOnChangeUnitPrice}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} spacing={2}>
          <FormController
            control={control}
            name={ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseQuantity}
          >
            <MuiTextField
              label={getLabel(
                projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsQuantity,
              )}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_75}
              showCloseIcon
              isMandatory
              type="number"
              requiredARIALabel={commonARIALabel.required}
              inputNumberWithoutDecimal
              positiveValuesOnly
            />
          </FormController>
        </MuiGrid>
        {currencyAndAmountFields}
      </MuiGrid>
    </MuiBox>
  );
};
